import { Grid } from "antd";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const CustomTooltip = ({ active, payload, label, type, metrics }: any) => {
  if (active && payload && payload.length) {
    // Format date
    const date = new Date(label);
    const formattedDate = `${date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    })} ${("0" + date.getHours()).slice(-2)}:${("0" + date.getMinutes()).slice(
      -2,
    )}`;

    // Custom styles
    const tooltipStyle = {
      backgroundColor: "#fff",
      borderRadius: "8px",
      padding: "4px 8px", // Reduced padding
    };
    const labelStyle = {
      fontWeight: "bold",
      marginBottom: "4px",
      marginTop: "1px",
      color: "#001100",
    };
    const valueStyle = { color: "#006ab3", margin: "2px" };

    if (type === "All") {
      return (
        <div className="line-graph-custom-tooltip" style={tooltipStyle}>
          <p className="label" style={labelStyle}>
            All Servers
          </p>
          {payload.map((entry: any, index: any) => (
            <p key={`item-${index}`} style={valueStyle}>
              <span style={{ color: entry.color, fontWeight: "bold" }}>
                {metrics === "Processing Time"
                  ? "Total Avg Processing Time: "
                  : "Total Processed Swings: "}{" "}
              </span>

              {metrics === "Processing Time"
                ? entry.value !== 0
                  ? entry.value.toFixed(2) + " secs"
                  : "0 seconds"
                : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return (
      <div className="line-graph-custom-tooltip" style={tooltipStyle}>
        <p className="label" style={labelStyle}>
          {formattedDate}
        </p>
        {payload.map((entry: any, index: any) => (
          <p key={`item-${index}`} style={valueStyle}>
            <span style={{ color: entry.color, fontWeight: "bold" }}>
              {entry.name}:{" "}
            </span>
            {metrics === "Processing Time"
              ? entry.value !== 0
                ? entry.value.toFixed(2) + " secs"
                : "0 seconds"
              : entry.value}
          </p>
        ))}
      </div>
    );
  }

  return null;
};

function CustomizedTick(props: any) {
  const { x, y, stroke, payload, fontSize } = props;

  // Format the time
  const date = new Date(payload.value);
  const formattedTime = `${("0" + date.getHours()).slice(-2)}:${(
    "0" + date.getMinutes()
  ).slice(-2)}`;

  // Format the date
  const day = String(date.getDate()).padStart(2, "0"); // Get day and pad with leading zero if necessary
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Get month (Note: Months are zero-based, so add 1) and pad with leading zero if necessary
  const formattedDate = `${day}/${month}`;
  // const fontSize = window.innerWidth < 1600 ? "12px" : "14px";

  if (props.timeInterval === "1 week") {
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="middle"
          fill="#666"
          style={{ fontSize: fontSize }}
        >
          {formattedDate}
        </text>
      </g>
    );
  }
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="middle"
        fill="#666"
        style={{ fontSize: fontSize }}
      >
        {formattedTime}
      </text>
      <text
        x={0}
        y={20}
        dy={16}
        textAnchor="middle"
        fill="#666"
        style={{ fontSize: fontSize }}
      >
        {formattedDate}
      </text>
    </g>
  );
}

export function LineGraph({
  graphData,
  serverKeys,
  lineColors,
  timeInterval,
  type,
  metrics,
}: any) {
  const defaultMinValue = 0;
  const defaultMaxValue = 50;

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const fontSize = screens.xxl ? 12 : screens.xl ? 8 : screens.lg ? 6 : 6;

  let minValue = defaultMinValue;
  let maxValue = defaultMaxValue;

  console.log("server", serverKeys, graphData);
  if (graphData?.length > 0 && serverKeys && serverKeys?.length > 0) {
    // Calculate the minimum and maximum values from the data
    const allValues = graphData.reduce((acc: any, cur: any) => {
      return [...acc, ...serverKeys.map((key: string) => cur[key] || 0)]; // Use 0 as fallback value if key does not exist
    }, []);
    minValue = Math.min(...allValues);
    maxValue = Math.max(...allValues);
  }

  console.log(minValue, maxValue, "VALUES");
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart
        width={500}
        height={350}
        data={graphData} // Use transformedData if graphData is available, otherwise use empty array
        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
      >
        {/* Render the axes and other components */}
        <CartesianGrid
          horizontal={true}
          vertical={false}
          strokeDasharray="3 3"
        />
        <XAxis
          padding="no-gap"
          dataKey="timeStamps"
          height={50}
          width={110}
          interval={0}
          tickCount={graphData.length}
          tick={
            <CustomizedTick timeInterval={timeInterval} fontSize={fontSize} />
          }
        />
        <YAxis
          tick={{ fontSize }}
          domain={[minValue, maxValue]} // Set the domain for the y-axis
        />
        <Tooltip content={<CustomTooltip type={type} metrics={metrics} />} />
        <Legend layout="horizontal" align="center" verticalAlign="top" />
        {Array.isArray(serverKeys) &&
          serverKeys.map((server: string, index: number) => (
            <Area
              key={index}
              type="monotone"
              dataKey={server}
              // baseValue={-50}
              stroke={
                lineColors[
                  Object.keys(lineColors)[
                    index % Object.keys(lineColors).length
                  ]
                ]
              } // Assigning colors from lineColors array
              fill={
                lineColors[
                  Object.keys(lineColors)[
                    index % Object.keys(lineColors).length
                  ]
                ] + "33"
              }
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 6 }}
            />
          ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}
